import {
  Caption1,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import {
  ChevronLeft16Regular,
  ChevronRight16Regular,
} from "@fluentui/react-icons";
import { useMemo } from "react";
import { FuiButton } from "@/components/fui-control";
import {
  addMonthsDay,
  fromDay,
  toDay,
  todayISO,
  type DayNumber,
  type ISODate,
} from "@/engine/date";
import { monthGrid } from "@/features/calendar-view/calendar-layout";
import { formatDate, formatDayNumber, weekdayNames } from "@/lib/date-format";

const WEEKDAYS_SHORT = weekdayNames("short");

const useStyles = makeStyles({
  container: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    userSelect: "none",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: tokens.spacingVerticalXS,
  },
  title: {
    fontWeight: tokens.fontWeightRegular,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  nav: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
  },
  weekRow: {
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
    textAlign: "center",
    marginBottom: "4px",
  },
  weekdayLabel: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground4,
    fontWeight: tokens.fontWeightRegular,
  },
  grid: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  daysRow: {
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
    gap: "2px",
  },
  dayCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "26px",
    width: "100%",
  },
  dayBtn: {
    width: "24px",
    height: "24px",
    borderRadius: tokens.borderRadiusCircular,
    border: "none",
    background: "transparent",
    fontSize: "11px",
    color: tokens.colorNeutralForeground1,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    transition: "background-color 0.15s ease",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  dayOut: {
    color: tokens.colorNeutralForeground4,
    opacity: 0.6,
  },
  daySelected: {
    backgroundColor: tokens.colorNeutralForeground1,
    color: tokens.colorNeutralBackground1,
    fontWeight: tokens.fontWeightRegular,
    ":hover": {
      backgroundColor: tokens.colorNeutralForeground1Hover,
    },
  },
  dayToday: {
    boxShadow: `inset 0 0 0 1px ${tokens.colorBrandStroke1}`,
  },
});

export interface MiniCalendarProps {
  /** Currently selected / anchored ISO date */
  value: ISODate;
  /** Fired when user picks a date */
  onChange: (iso: ISODate) => void;
}

export function MiniCalendar({ value, onChange }: MiniCalendarProps) {
  const styles = useStyles();
  const monthKey = value.slice(0, 7);
  const today = todayISO();
  const grid = useMemo(() => monthGrid(value), [value]);

  const go = (dir: -1 | 1) => {
    onChange(fromDay(addMonthsDay(toDay(value), dir)));
  };

  const monthTitle = useMemo(() => formatDate(value, "yearMonth"), [value]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Caption1 className={styles.title}>{monthTitle}</Caption1>
        <div className={styles.nav}>
          <FuiButton
            appearance="subtle"
            size="small"
            icon={<ChevronLeft16Regular style={{ fontSize: 13 }} />}
            aria-label="이전 달"
            onClick={() => go(-1)}
          />
          <FuiButton
            appearance="subtle"
            size="small"
            icon={<ChevronRight16Regular style={{ fontSize: 13 }} />}
            aria-label="다음 달"
            onClick={() => go(1)}
          />
        </div>
      </div>

      <div className={styles.weekRow}>
        {WEEKDAYS_SHORT.map((w) => (
          <span key={w} className={styles.weekdayLabel}>
            {w}
          </span>
        ))}
      </div>

      <div className={styles.grid}>
        {grid.map((row: DayNumber[], rIdx: number) => (
          <div key={rIdx} className={styles.daysRow}>
            {row.map((day: DayNumber) => {
              const iso = fromDay(day);
              const inMonth = iso.slice(0, 7) === monthKey;
              const isSelected = iso === value;
              const isToday = iso === today;

              return (
                <div key={day} className={styles.dayCell}>
                  <button
                    type="button"
                    className={mergeClasses(
                      styles.dayBtn,
                      !inMonth && styles.dayOut,
                      isToday && styles.dayToday,
                      isSelected && styles.daySelected,
                    )}
                    onClick={() => onChange(iso)}
                  >
                    {formatDayNumber(day, "day")}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
